import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const highlights = [
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Problem Solver",
      description: "Specialized in debugging complex systems and finding elegant solutions to technical challenges.",
      gradient: "from-purple-500 to-blue-500"
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      title: "System Architect",
      description: "Experience building scalable applications with clean architecture and maintainable code.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: "Innovation Focused",
      description: "Passionate about implementing modern technologies while maintaining backward compatibility.",
      gradient: "from-purple-600 to-pink-500"
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: "Team Player",
      description: "Strong collaboration skills with emphasis on code quality and knowledge sharing.",
      gradient: "from-green-500 to-emerald-500"
    }
  ];

  const experience = [
    {
      role: "Full-Stack Developer",
      period: "2020 - Present",
      description: "Building enterprise applications with React, Node.js, and Oracle databases. Specializing in payment integrations and election management systems.",
      technologies: ["React", "Node.js", "Oracle", "Prisma"]
    },
    {
      role: "Backend Specialist",
      period: "2018 - 2020",
      description: "Developed robust PL/SQL procedures and Oracle Forms applications for banking and financial systems.",
      technologies: ["PL/SQL", "Oracle Forms", "Java", "REST APIs"]
    },
    {
      role: "Payment Integration Engineer",
      period: "2019 - Present",
      description: "Implemented mobile money solutions for major Ghanaian telecom providers with seamless user experiences.",
      technologies: ["Paystack", "MTN MoMo", "Vodafone", "APIs"]
    }
  ];

  const skills = [
    { category: "Frontend Development", percentage: 90 },
    { category: "Backend Development", percentage: 95 },
    { category: "Database Management", percentage: 90 },
    { category: "DevOps & Deployment", percentage: 75 }
  ];

  return (
    <section ref={sectionRef} className="relative min-h-screen bg-white py-20 px-6 overflow-hidden">
      
      {/* Subtle Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-96 h-96 bg-purple-100/30 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-100/30 rounded-full filter blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-pink-100/20 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto max-w-7xl mt-16">
        
        {/* Section Header */}
        <div className={`text-center mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block px-4 py-2 bg-purple-50 border border-purple-200 rounded-full text-purple-600 text-sm font-semibold mb-4 uppercase tracking-wide">
            About Me
          </span>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Crafting Digital{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
              Experiences
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-blue-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            A passionate developer dedicated to transforming ideas into reality through 
            elegant code and innovative solutions.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          
          {/* Left Column - Profile & Bio */}
          <div className={`space-y-8 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            
            {/* Profile Card */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              {/* Profile Avatar */}
              <div className="relative w-32 h-32 mx-auto mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full animate-spin-slow"></div>
                <div className="absolute inset-1 bg-white rounded-full flex items-center justify-center">
                  <svg className="w-16 h-16 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              </div>

              <h3 className="text-3xl font-bold text-gray-900 mb-2 text-center">Godfrey</h3>
              <p className="text-lg text-purple-600 font-semibold text-center mb-6">Full-Stack Software Engineer</p>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="text-3xl font-bold text-purple-600">5+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="text-3xl font-bold text-purple-600">50+</div>
                  <div className="text-sm text-gray-600">Projects Done</div>
                </div>
              </div>

              {/* Location & Status */}
              <div className="space-y-3 text-sm border-t border-gray-200 pt-6">
                <div className="flex items-center justify-center gap-2 text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Accra, Ghana</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                  </span>
                  <span className="font-semibold">Available for opportunities</span>
                </div>
              </div>
            </div>

            {/* My Journey */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                My Journey
              </h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  I'm a passionate <span className="font-semibold text-purple-600">Full-Stack Software Engineer</span> with extensive experience 
                  in building robust web applications and enterprise solutions. My expertise 
                  spans across modern frontend frameworks, backend development, and database 
                  management.
                </p>
                <p>
                  I specialize in creating seamless <span className="font-semibold text-purple-600">payment integration systems</span> for Ghana's 
                  major telecommunications networks, developing comprehensive election management 
                  platforms, and building scalable applications using React, Node.js, and Oracle 
                  databases.
                </p>
                <p>
                  My approach to development is <span className="font-semibold text-purple-600">methodical and focused</span> on preserving existing 
                  functionality while implementing new features. I believe in writing clean, 
                  maintainable code and following best practices to ensure long-term project 
                  success.
                </p>
              </div>
            </div>

            {/* Skills Progress */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Expertise Level</h3>
              <div className="space-y-6">
                {skills.map((skill, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-700 font-semibold">{skill.category}</span>
                      <span className="text-purple-600 font-bold">{skill.percentage}%</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-600 to-blue-600 rounded-full transition-all duration-1000 ease-out"
                        style={{ 
                          width: isVisible ? `${skill.percentage}%` : '0%',
                          transitionDelay: `${index * 100}ms`
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Highlights & Values */}
          <div className={`space-y-8 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            
            <h3 className="text-3xl font-bold text-gray-900 mb-8">What I Bring to the Table</h3>
            
            {/* Highlights Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div 
                  key={index}
                  className="group bg-white border-2 border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl hover:border-purple-300 transition-all duration-300 hover:-translate-y-2"
                >
                  <div className={`inline-flex p-4 bg-gradient-to-r ${highlight.gradient} text-white rounded-xl mb-4 transform group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                    {highlight.icon}
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">
                    {highlight.title}
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    {highlight.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Core Values */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <div className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                Core Values
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {['Quality', 'Innovation', 'Reliability', 'Collaboration'].map((value, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-3 text-gray-700 bg-white/80 rounded-xl p-4 border border-purple-200 hover:bg-white hover:shadow-md transition-all duration-300"
                  >
                    <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Fun Fact */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="p-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                What Drives Me
              </h3>
              <p className="text-gray-700 leading-relaxed">
                What drives me is solving complex technical challenges, whether it's debugging 
                intricate PL/SQL procedures, implementing real-time voting systems, or integrating 
                multiple payment APIs to create seamless user experiences. I thrive on turning 
                challenging problems into elegant, maintainable solutions.
              </p>
            </div>
          </div>
        </div>

        {/* Experience Timeline */}
        <div className={`mb-20 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">
              Professional Journey
            </h3>
            <p className="text-gray-600 text-lg">A timeline of growth and achievement</p>
          </div>
          
          <div className="space-y-8 max-w-4xl mx-auto">
            {experience.map((exp, index) => (
              <div 
                key={index}
                className="group relative"
              >
                {/* Timeline Line */}
                {index !== experience.length - 1 && (
                  <div className="absolute left-8 top-20 w-0.5 h-full bg-gradient-to-b from-purple-300 to-blue-300 hidden md:block"></div>
                )}
                
                <div className="flex gap-6">
                  {/* Timeline Dot */}
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-white border-2 border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl hover:border-purple-300 transition-all duration-300 group-hover:-translate-y-1">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                      <h4 className="text-2xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                        {exp.role}
                      </h4>
                      <span className="text-purple-600 text-sm font-bold bg-purple-50 px-4 py-2 rounded-full border border-purple-200 mt-2 md:mt-0">
                        {exp.period}
                      </span>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      {exp.description}
                    </p>
                    
                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2">
                      {exp.technologies.map((tech, techIndex) => (
                        <span 
                          key={techIndex}
                          className="px-3 py-1 bg-gray-50 border border-gray-300 rounded-full text-sm text-gray-700 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className={`text-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-3xl p-12 shadow-xl">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">
              Let's Build Something Amazing
            </h3>
            <p className="text-gray-700 mb-8 text-xl leading-relaxed max-w-2xl mx-auto">
              I'm always excited to collaborate on innovative projects and tackle new challenges. 
              Whether you have an idea or just want to connect, let's make it happen!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="group px-10 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <span className="flex items-center justify-center gap-2">
                  Get In Touch
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </Link>
              <Link 
                to="/projects"
                className="px-10 py-4 bg-white border-2 border-gray-900 text-gray-900 rounded-xl font-semibold text-lg hover:bg-gray-900 hover:text-white shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                View My Work
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default About;